import { useQuery } from "@tanstack/react-query";
import { format, parse, subDays } from "date-fns";
import { memoize } from "lodash";
import { useMemo } from "react";
import { debugLog, errorLog } from "../../../logger";
import getAccountGroupPerformancesV4, { accountGroupsV4PerformanceSpecificKeyMaker, } from "../requests/getAccountGroupPerformancesV4";
import { DateStringYYYYMMDD, } from "../requests/getSingleAccountGroupPerformanceV4";
import { cacheTimeMs, dateFormat, staleTimeMs } from "../Shared";
import { calculateDateRangeFromTimeFrame } from "../utilities/timeframes";
import { GroupNotFoundError } from "./Types";
function isNotNull(x) {
    return x !== null;
}
export class DateRangeNullError extends Error {
    constructor(timeFrame) {
        super(`dateRange is null for ${timeFrame}`);
    }
}
export class DateRangeEmptyError extends Error {
    constructor() {
        super("Date Range is Empty");
    }
}
export function timeFrameFromToday(timeFrame, customTimeFrame) {
    // do from yesterday b/c today has no info (we batch run at night)
    const yesterday = subDays(new Date(), 1);
    const dateRange = memoizedCalculateDateRangeFromTimeFrame(timeFrame, customTimeFrame, yesterday);
    return dateRange;
}
export const memoizedCalculateDateRangeFromTimeFrame = memoize(calculateDateRangeFromTimeFrame, (timeFrame, customTimeFrame, yesterday) => {
    const hour = format(new Date(), "HH");
    if (timeFrame === "Custom") {
        if (customTimeFrame === null) {
            throw new Error("timeFrame === Custom but customTimeFrame is null [memoizedCalculateDateRangeFromTimeFrame]");
        }
        return `${hour}-${timeFrame}${customTimeFrame.startDate}-${customTimeFrame.endDate}`;
    }
    // only memoize in 1 hour chunks (this is `HH`) ex: https://date-fns.org/v2.29.3/docs/format
    return `${hour}-${timeFrame}${format(yesterday, dateFormat)}`;
});
export const memoizedGenDateRangeObject = memoize((startDate, endDate) => ({
    startDate,
    endDate,
}), (s, e) => `${s}${e}`);
export default function useGetDatesForTimeFrameV4(groupId, timeFrame, customTimeFrame, clientId, authConfig) {
    const nullRequestBody = {
        dateRanges: null,
        feeMethod: "grossOfFees",
        returnMethod: "cumulative",
    };
    // if we are asking for "All" then we need to get the date the account was opened. We do this
    // by sending a request with a `dateRanges` set to null. From the response data grab
    // `dateRangePoints[0].dateRange` because the backend will set `startDate` to the account open
    // date and `endDate` to today.
    const requestEnabled = timeFrame === "ALL" &&
        authConfig !== null &&
        groupId !== null &&
        clientId !== null;
    const accountsData = useQuery({
        queryKey: clientId === null
            ? undefined
            : accountGroupsV4PerformanceSpecificKeyMaker(clientId, nullRequestBody),
        queryFn: async () => authConfig === null || clientId === null
            ? undefined
            : getAccountGroupPerformancesV4(clientId, nullRequestBody, authConfig),
        enabled: requestEnabled,
        cacheTime: cacheTimeMs,
        staleTime: staleTimeMs,
    });
    const result = useMemo(() => {
        if (timeFrame !== "ALL") {
            const dateRange = timeFrameFromToday(timeFrame, customTimeFrame);
            if (dateRange !== null) {
                return { data: dateRange, isLoading: false, hasError: false };
            }
            else {
                errorLog("[useGetDatesForTimeFrameV4]", `dateRange is null for ${timeFrame} for ${groupId}`);
                return {
                    data: undefined,
                    isLoading: false,
                    hasError: true,
                    error: new DateRangeNullError(timeFrame),
                };
            }
        }
        if (accountsData.data === undefined && accountsData.isLoading) {
            return { data: undefined, isLoading: true, hasError: false };
        }
        if (accountsData.data === undefined && accountsData.isError) {
            debugLog("useGetDatesForTimeFrameV4", `useQuery has error with ${timeFrame} for ${groupId} - ${accountsData.error}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: accountsData.error,
            };
        }
        if (accountsData.data === undefined) {
            debugLog("useGetDatesForTimeFrameV4", `data is undefined but not loading or error with ${timeFrame} for ${groupId}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new Error("Unknown error"),
            };
        }
        if (groupId === null) {
            debugLog("useGetDatesForTimeFrameV4", `group is null with ${timeFrame} for ${groupId}`);
            return {
                data: undefined,
                isLoading: true,
                hasError: false,
            };
        }
        const group = accountsData.data.find((g) => g.accountGroupId === groupId);
        if (group === undefined) {
            errorLog("[useGetDatesForTimeFrameV4]", `Can't find ${groupId} in response`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new GroupNotFoundError(groupId ?? "null"),
            };
        }
        const todayAsString = format(new Date(), dateFormat);
        const dateRanges = group.dateRangePoints
            .filter(isNotNull)
            .map((x) => x.dateRange)
            .filter(isNotNull);
        if (dateRanges.length === 0) {
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new DateRangeEmptyError(),
            };
        }
        let startDate = dateRanges.map((x) => x.startDate).sort()[0];
        let endDate = dateRanges
            .map((x) => x.endDate)
            .sort()
            .slice(-1)[0];
        if (startDate === undefined) {
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new Error("startDate not found"),
            };
        }
        // today has no useful date so switch to yesterday
        if (endDate === todayAsString) {
            endDate = DateStringYYYYMMDD.parse(format(subDays(new Date(), 1), dateFormat));
        }
        // we can't have the dates be equal, subtract a day from start date
        if (startDate === endDate) {
            startDate = DateStringYYYYMMDD.parse(format(subDays(parse(startDate, dateFormat, new Date()), 1), dateFormat));
        }
        if (endDate === undefined) {
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new Error("endDate not found"),
            };
        }
        const dateRange = memoizedGenDateRangeObject(startDate, endDate);
        return {
            data: dateRange,
            isLoading: false,
            hasError: false,
        };
    }, [
        accountsData.data,
        accountsData.error,
        accountsData.isError,
        accountsData.isLoading,
        groupId,
        timeFrame,
        customTimeFrame,
    ]);
    return result;
}
//# sourceMappingURL=useGetDatesForTimeFrameV4.js.map